import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { getUpgradePricingDiscount } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { isExperienceItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import { floatify } from 'lib/maths/mathUtils'

type HotelPricedRate = Pick<App.BasePropertyRate, 'currency' | 'totals' | 'luxPlusTotals' | 'bundle' | 'paymentMethod' | 'token'> & {
  cheapestFlightPrice?: App.EnrichedPropertyRate['cheapestFlightPrice']
}

function getFlightOverheadPrice(rate: HotelPricedRate): number {
  if (typeof rate.cheapestFlightPrice === 'number') return rate.cheapestFlightPrice
  return 0
}

/**
 * Session sell/LP are hotel-only and exclusive of property fees. Mandatory bundle
 * tickets and flights stay off `sellTotal` so svc-order does not double-charge them.
 */
export function getHotelPricing(rate: HotelPricedRate): CreateEnquirySessionPayload['pricing'] {
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  const [hotelSellingPrice, hotelLuxPlusSellingPrice] = rate.bundle ? [
    rate.bundle.hotelSellingPrice,
    rate.bundle.hotelLuxPlusSellingPrice || undefined,
  ] : [
    floatify(rate.totals.sellingPrice - flightOverheadPrice),
    rate.luxPlusTotals?.sellingPrice
      ? floatify(rate.luxPlusTotals.sellingPrice - flightOverheadPrice)
      : undefined,
  ]

  const propertyFees = rate.totals.propertyFees || undefined
  // Collected by the property, like propertyFees, so it stays off sellTotal. Sent so the cart
  // can disclose it before a checkout enquiry has run and restamped the session.
  const surchargePayableAtProperty = rate.totals.surchargePayableAtProperty || undefined
  // Contract: luxPlusTotal is exclusive of property fees, matching sellTotal.
  const luxPlusTotal = hotelLuxPlusSellingPrice

  return {
    currency: rate.currency,
    sellTotal: hotelSellingPrice,
    ...(propertyFees != null ? { propertyFees } : {}),
    ...(surchargePayableAtProperty != null ? { surchargePayableAtProperty } : {}),
    ...(luxPlusTotal != null ? { luxPlusTotal } : {}),
    paymentMethod: rate.paymentMethod.type,
    ...(rate.token ? { priceToken: rate.token } : {}),
  }
}

export function getAccommodationV2CartPricing(
  rate: App.EnrichedPropertyRate,
): CreateEnquirySessionPayload['pricing'] {
  return getHotelPricing(rate)
}

function mapOccupancy(occupancy: App.Occupants): CreateEnquirySessionPayload['occupancy'] {
  return {
    adults: occupancy.adults,
    ...(occupancy.children != null ? { children: occupancy.children } : {}),
    ...(occupancy.teenagers != null ? { teenagers: occupancy.teenagers } : {}),
    ...(occupancy.infants != null ? { infants: occupancy.infants } : {}),
    ...(occupancy.childrenAge?.length ? { childrenAges: occupancy.childrenAge } : {}),
  }
}

function mapSupplierMetaData(
  rate: App.EnrichedPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        rateGroupId: rate.service.rateGroupId,
        bedGroupId: rate.service.bedGroups[0]?.id,
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ratePlanId: rate.service.ratePlanId,
      rateGroupId: rate.groupKey,
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

function mapSessionBundle(
  bundle: {
    bundleId: string
    tickets: Array<{ experienceId: string, productId: string, price: number }>
  },
  optIn?: true,
): NonNullable<CreateEnquirySessionPayload['bundle']> {
  return {
    bundleId: bundle.bundleId,
    tickets: bundle.tickets.map(({ experienceId, productId, price }) => ({
      experienceId,
      productId,
      ticketPrice: price,
    })),
    ...(optIn ? { optIn: true } : {}),
  }
}

function mapCartBundleTickets(
  item: App.Checkout.ExperienceItem | App.Checkout.TransferItem,
): App.Property.Bundle['tickets'] {
  let tickets: App.Property.Bundle['tickets']
  if (isTransferItem(item)) {
    const option = item.transfer.option
    if (!option) {
      throw new Error(`Missing bundled transfer option for cart item ${item.itemId}`)
    }
    tickets = [{ experienceId: item.experienceId, productId: option.id, price: option.price }]
  } else {
    tickets = item.tickets.map(ticket => ({
      experienceId: item.experienceId,
      productId: ticket.ticketId,
      price: ticket.price,
    }))
  }

  if (!tickets.length || tickets.some(ticket => !ticket.experienceId || !ticket.productId || !Number.isFinite(ticket.price))) {
    throw new Error(`Missing or invalid bundle ticket data for cart item ${item.itemId}`)
  }

  // V2 bundled children belong to one accommodation item, so prices need no allocation.
  return tickets
}

function mapCartItemBundles(
  item: App.Checkout.AccommodationV2Item,
  cartItems: ReadonlyArray<App.Checkout.AnyItem>,
): Pick<CreateEnquirySessionPayload, 'bundle' | 'optInBundles'> {
  const bundleIds = item.accommBundleIds
  if (!bundleIds?.length) return {}

  if (!item.bundleOptin && bundleIds.length > 1) {
    throw new Error(`Multiple mandatory bundles for cart item ${item.itemId}`)
  }

  const children = cartItems.filter((child): child is App.Checkout.ExperienceItem | App.Checkout.TransferItem => (
    (isExperienceItem(child) || isTransferItem(child)) && !!child.bundledItemIds?.includes(item.itemId)
  ))
  const bundles = bundleIds.map(bundleId => {
    const bundledChildren = children.filter(child => child.isAccomBundle === bundleId)
    if (!bundleId || !bundledChildren.length) {
      throw new Error(`Missing children for bundle ${bundleId} on cart item ${item.itemId}`)
    }
    if (bundledChildren.some(child => !!child.bundleOptin !== !!item.bundleOptin)) {
      throw new Error(`Conflicting opt-in flags for bundle ${bundleId} on cart item ${item.itemId}`)
    }

    return mapSessionBundle({
      bundleId,
      tickets: bundledChildren.flatMap(mapCartBundleTickets),
    }, item.bundleOptin ? true : undefined)
  })

  return item.bundleOptin ? { optInBundles: bundles } : { bundle: bundles[0] }
}

export interface BuildEnquirySessionPayloadParams {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  checkIn: string
  checkOut: string
  region: string
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>>
  /** Shopped LuxPlus+ eligibility. Checkout enquiry gates member-rate price changes on this. */
  isLuxPlusUser?: boolean
  /** Must match the `experiments.lppMemberPricingEnabled` the order is created with. */
  lppMemberPricingEnabled: boolean
  /** Loyalty package upgrade on this rate. Only an applied upgrade (booking the target) is stamped. */
  loyaltyUpgrade?: App.Checkout.PropertyRateLoyaltyUpgrade
}

/**
 * A rate's pricing on the session's hotel-only basis, for a loyalty upgrade. Rate totals carry
 * mandatory bundle tickets and flights, which accomV2 charges as separate items.
 */
export function getHotelOnlyPropertyRatePricing(rate: HotelPricedRate): App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'] {
  const { sellTotal, luxPlusTotal } = getHotelPricing(rate)
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  // Bundle hotel prices are already un-marked; otherwise strip the flights off the un-marked total.
  const hotelBase = (totals: App.PropertyRateTotals, hotelSell: number) => rate.bundle
    ? hotelSell
    : floatify((totals.baseSellingPrice ?? totals.sellingPrice) - flightOverheadPrice)

  return {
    totals: { ...rate.totals, sellingPrice: sellTotal, baseSellingPrice: hotelBase(rate.totals, sellTotal) },
    ...(rate.luxPlusTotals && luxPlusTotal != null && {
      luxPlusTotals: { ...rate.luxPlusTotals, sellingPrice: luxPlusTotal, baseSellingPrice: hotelBase(rate.luxPlusTotals, luxPlusTotal) },
    }),
  }
}

/**
 * The applied upgrade's discount (target price minus source price) and its source rate. Mirrors the
 * legacy bedbank enquiry, which only sends the discount on the target rate's enquiry.
 */
export function buildSessionLoyaltyUpgrade(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined,
  isLuxPlusUser: boolean | undefined,
  lppMemberPricingEnabled: boolean,
): Pick<CreateEnquirySessionPayload, 'loyaltyPackageUpgradeDiscount' | 'loyaltyUpgradeSource'> {
  if (!upgrade || upgrade.applied === false) return {}

  // On the basis svc-accommodation prices the session: member only with LPP member pricing on.
  const discount = getUpgradePricingDiscount(upgrade, !!isLuxPlusUser && lppMemberPricingEnabled)
  return {
    ...(discount && { loyaltyPackageUpgradeDiscount: discount }),
    loyaltyUpgradeSource: {
      serviceType: upgrade.source.serviceType,
      offerId: upgrade.source.serviceOfferId,
      propertyId: upgrade.source.propertyId,
      roomId: upgrade.source.roomId,
      roomRateId: upgrade.source.serviceRateId,
      packageId: upgrade.source.servicePackageId,
      sessionId: upgrade.sourceBooking?.sessionId,
    },
  }
}

/**
 * The session's `createdContext`. `lppMemberPricingEnabled` is sent explicitly because
 * svc-accommodation treats an absent value as on: a member would then be priced at the
 * bedbank member total while svc-order, with the experiment off, books the public one.
 */
export function buildSessionCreatedContext(
  isLuxPlusUser: boolean | undefined,
  lppMemberPricingEnabled: boolean,
): Pick<CreateEnquirySessionPayload, 'createdContext'> {
  return {
    createdContext: {
      userContext: {
        ...(isLuxPlusUser && { isLuxPlusUser: true }),
        lppMemberPricingEnabled,
      },
    },
  }
}

/**
 * Builds a svc-accommodation enquiry-session create payload from a unified property rate.
 * Rate identity uses optionId + supplier internalRateId — never the ephemeral unified rateId.
 *
 * Mandatory `rate.bundle` and selected optional bundles are independent: both can sit on the
 * same session (`bundle` + `optInBundles`).
 */
export function buildEnquirySessionPayload({
  rate,
  occupancy,
  checkIn,
  checkOut,
  region,
  selectedOptionalBundles,
  isLuxPlusUser,
  lppMemberPricingEnabled,
  loyaltyUpgrade,
}: BuildEnquirySessionPayloadParams): CreateEnquirySessionPayload {
  const mandatoryBundle = rate.bundle

  return {
    pricing: getHotelPricing(rate),
    property: { propertyId: rate.propertyId },
    room: { roomId: rate.roomId },
    occupancy: mapOccupancy(occupancy),
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(mandatoryBundle && {
      bundle: mapSessionBundle(mandatoryBundle),
    }),
    ...(selectedOptionalBundles?.length && {
      optInBundles: selectedOptionalBundles.map(bundle => mapSessionBundle(bundle, true)),
    }),
    ...buildSessionLoyaltyUpgrade(loyaltyUpgrade, isLuxPlusUser, lppMemberPricingEnabled),
    ...buildSessionCreatedContext(isLuxPlusUser, lppMemberPricingEnabled),
  }
}

export function buildEnquirySessionPayloadFromCartItem(
  item: App.Checkout.AccommodationV2Item,
  region: string,
  isLuxPlusUser: boolean,
  cartItems: ReadonlyArray<App.Checkout.AnyItem>,
  lppMemberPricingEnabled: boolean,
): CreateEnquirySessionPayload {
  return {
    pricing: {
      currency: item.currency,
      sellTotal: item.sellTotal,
      ...(item.propertyFees != null ? { propertyFees: item.propertyFees } : {}),
      ...(item.surchargePayableAtProperty != null
        ? { surchargePayableAtProperty: item.surchargePayableAtProperty }
        : {}),
      ...(item.luxPlusTotal != null ? { luxPlusTotal: item.luxPlusTotal } : {}),
      paymentMethod: item.paymentMethod,
      ...(item.token ? { priceToken: item.token } : {}),
    },
    property: { propertyId: item.propertyId },
    room: { roomId: item.roomId },
    occupancy: mapOccupancy(item.occupancy),
    supplierMetaData: {
      internalService: item.internalService,
      selectedRate: {
        optionId: item.optionId,
        internalRateId: item.internalRateId,
        ...(item.rateGroupId ? { rateGroupId: item.rateGroupId } : {}),
        ...(item.bedGroupId ? { bedGroupId: item.bedGroupId } : {}),
        ...(item.ratePlanId ? { ratePlanId: item.ratePlanId } : {}),
      },
      internalPropertyId: item.internalPropertyId,
      internalRoomId: item.internalRoomId,
    },
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    region,
    currency: item.currency,
    ...mapCartItemBundles(item, cartItems),
    ...buildSessionLoyaltyUpgrade(item.loyaltyPropertyRateUpgrade, isLuxPlusUser, lppMemberPricingEnabled),
    ...buildSessionCreatedContext(isLuxPlusUser, lppMemberPricingEnabled),
  }
}
